import { REFETCH_INTERVAL_SLOW } from '../confs/app';
import { useAppSettingStore } from '../store/app-store';
import { useAuth } from './use-auth';
import { ApiInstance, UserAssetsInfoParams } from '@/libs/sdk/api';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

// The portfolio is mark-to-market (no event for price moves), the block listener
// writes the projection after the trade receipt, and a stream opened before login
// stays anonymous (no user:asset_changed). So the DPIN build does not rely on SSE
// alone: it polls every 30 s while the tab is visible. Other modes keep 15 s.
export const SIGNED_ASSETS_VISIBLE_POLL_MS = 30_000;
export const signedAssetsRefreshOptions = (mode = import.meta.env.MODE) => ({
  refetchInterval: mode === 'dpin-bsc' ? SIGNED_ASSETS_VISIBLE_POLL_MS : REFETCH_INTERVAL_SLOW,
  refetchIntervalInBackground: false,
});

export const useSignedUserAssets = (api: ApiInstance, params?: UserAssetsInfoParams) => {
  const { data } = useAuth();
  const uid = data?.uid;
  const baseToken = useAppSettingStore(state => state.baseToken);
  const { baseTokenAddress = baseToken.address } = params || {};
  const query = useQuery({
    enabled: !!uid,
    staleTime: REFETCH_INTERVAL_SLOW,
    ...signedAssetsRefreshOptions(),
    queryKey: ['SignedUserAssets', api, uid, baseTokenAddress, params],
    queryFn: async () => {
      const { data } = await api.market.getUserAssetsInfo({ ...params, uid: uid!, baseTokenAddress });
      return data;
    },
  });
  useEffect(() => {
    if (!query.isEnabled) return;
    const reftch = () => query.refetch({ cancelRefetch: false });
    api.sse.addEventListener('user:asset_changed', reftch);
    api.sse.addEventListener('base:connected', reftch);
    return () => {
      api.sse.removeEventListener('user:asset_changed', reftch);
      api.sse.removeEventListener('base:connected', reftch);
    };
  }, [query.isEnabled, api.sse]);
  return query;
};
